import * as F from './fixed'
import type { Fixed } from './fixed'
import * as V from './vec'
import type { Vec } from './vec'

/**
 * Versão da FÍSICA.
 *
 * Diferente da versão do formato de replay: esta identifica o COMPORTAMENTO.
 * Qualquer mudança que altere onde as bolas param — atrito, restituição, passo
 * de integração, geometria — obriga a incrementar este número.
 *
 * Sem isso, um replay gravado hoje seria reproduzido amanhã com uma física
 * diferente e daria outro vencedor. O teste em `fixtures.test.ts` compara o
 * digest da bateria com o declarado abaixo e falha se alguém mudar a física
 * sem incrementar aqui.
 */
export const ENGINE_VERSION = 7

/**
 * Impressão digital da física desta versão.
 *
 * É o digest da bateria de referência. Um verificador independente roda a
 * bateria, compara com este valor, e sabe se está executando a mesma física
 * que gravou o replay.
 */
export const PHYSICS_DIGEST = 'c7e6365d'

/**
 * Geometria da mesa e constantes físicas.
 *
 * Medidas de uma mesa de 7 pés (a mais comum para 8-Ball), em metros. Todos os
 * valores viram Q16.16 UMA VEZ aqui — é o único lugar do módulo onde um número
 * real entra, e é o que mantém o float fora do laço da simulação.
 *
 * Origem no canto inferior esquerdo do pano; x cresce para a direita, y para
 * cima.
 */

/** Área de jogo: 1.98m × 0.99m. */
export const WIDTH: Fixed = F.from(1.98)
export const HEIGHT: Fixed = F.from(0.99)

/** Bola de 57.15mm de diâmetro. */
export const BALL_RADIUS: Fixed = F.from(0.0285_75)
export const BALL_DIAMETER: Fixed = BALL_RADIUS * 2

/** Distância entre centros no momento do contato. */
export const CONTACT_DISTANCE: Fixed = BALL_DIAMETER
export const CONTACT_DISTANCE_SQ: Fixed = F.sqr(CONTACT_DISTANCE)

/** Raio de captura da caçapa, medido do centro dela ao centro da bola. */
export const POCKET_RADIUS: Fixed = F.from(0.05)
export const POCKET_RADIUS_SQ: Fixed = F.sqr(POCKET_RADIUS)

/**
 * Gravidade, em m/s². Entra nas três frições abaixo.
 */
export const GRAVITY: Fixed = F.from(9.81)

/*
 * COEFICIENTES DE ATRITO — valores da literatura, não calibrados a olho.
 *
 * A v2 usava uma desaceleração única de 0,45 m/s², escolhida pelo toque. A v3
 * usa o modelo de Coulomb com três coeficientes distintos, porque é isso que
 * separa deslizar de rolar — e é essa separação que produz masse, curva no
 * percurso e recuo que sobrevive à distância.
 *
 * Faixas medidas em mesa (Alciatore, Shepard): deslizamento 0,15–0,4, rolamento
 * 0,005–0,015, bola-bola 0,03–0,08.
 *
 * ATENÇÃO AO QUE ISSO MUDA NO JOGO: μr·g dá 0,098 m/s², contra os 0,45 da v2.
 * A bola anda MUITO mais. A 1 m/s ela percorreria 5 metros em linha reta, e a
 * mesa tem 1,98 — ou seja, tacadas suaves passam a cruzar a mesa várias vezes
 * antes de parar. É o comportamento de uma mesa de verdade, e foi uma escolha
 * explícita de realismo sobre o toque anterior.
 */

/** Deslizamento bola-pano, enquanto a bola ainda patina. */
export const MU_SLIDE: Fixed = F.from(0.2)

/**
 * Rolamento bola-pano, depois que ela pega o pano.
 *
 * Medido: Mathavan, Jackson & Parkin (Am. J. Phys. 77, 2009) filmaram bolas
 * rolando e acharam desaceleração de 0,124 a 0,126 m/s², ou seja 0,0127 a
 * 0,0129 de g. O 0,01 anterior era chute na faixa de Marlow (0,011 a 0,024) e
 * deixava as bolas rolarem 20% mais longe que numa mesa de verdade.
 *
 * 0,0124 e não 0,0128 porque o que se calibra é a desaceleração EFETIVA: o
 * fator de rolagem é arredondado para baixo em Q16.16 a cada passo, e isso
 * soma ~0,004 m/s² à conta. `medidas.test.ts` mede o resultado, não a
 * constante.
 */
export const MU_ROLL: Fixed = F.from(0.0124)

/** Atrito do efeito lateral girando no lugar. */
export const MU_SPIN: Fixed = F.from(0.044)

/** Atrito entre bolas — é ele que produz o *throw*. */
export const MU_BALL: Fixed = F.from(0.06)

/**
 * Abaixo deste módulo de velocidade relativa, a bola PASSA a rolar.
 *
 * Em contínuo `|u|` chega exatamente a zero; num passo fixo, nunca — ela
 * passaria do ponto e ficaria oscilando entre deslizar e rolar, e a oscilação
 * é diferença de arredondamento, ou seja, divergência entre máquinas.
 *
 * Grudar abaixo de um limiar resolve. E o limiar VIRA PARTE DO CONTRATO DA
 * FÍSICA: mudá-lo muda o digest e invalida os replays da v3. Não é constante de
 * ajuste, é número gravado em pedra.
 */
export const ROLL_THRESHOLD: Fixed = F.from(0.005)

/** Abaixo disto a bola é considerada parada, evitando deslizar para sempre. */
export const REST_SPEED: Fixed = F.from(0.01)
export const REST_SPEED_SQ: Fixed = F.sqr(REST_SPEED)

/**
 * Quanto da velocidade sobra ao bater na tabela.
 *
 * Medido: no mesmo estudo de Mathavan, o ajuste linear de velocidade de
 * saída contra entrada, de 0,28 a 3,5 m/s (a faixa do jogo), dá 0,818. Em
 * velocidade baixa a inclinação chega a 0,91; a queda em velocidade alta é
 * deformação da borracha. O 0,75 anterior devolvia bolas lentas demais da
 * tabela, e um jogador que conta tabelas pela mesa real errava sempre curto.
 */
export const CUSHION_RESTITUTION: Fixed = F.from(0.82)

/** Quanto da velocidade sobra numa colisão entre bolas. */
export const BALL_RESTITUTION: Fixed = F.from(0.95)

/**
 * Fração da componente normal que TROCA de bola numa colisão.
 *
 * Não é a restituição, e confundir os dois foi um erro de fator 2 que viveu
 * meses no motor. Para massas iguais a bola parada leva `(1+e)/2` e a que bateu
 * retém `(1−e)/2`. O código antigo multiplicava a aproximação por `e` e aplicava
 * esse impulso INTEIRO nas duas bolas, então a velocidade relativa mudava em
 * `2·impulso` e a restituição efetiva virava `2e−1` — com `e = 0,95` declarado,
 * a mesa jogava com 0,90.
 *
 * O sintoma era discreto e caro: a branca guardava 5% da velocidade normal em
 * vez de 2,5%, então numa batida quase cheia ela seguia para frente muito mais
 * do que deveria, e a energia dissipada era quase o dobro da certa. O momento
 * continuava conservado — o erro é simétrico entre as duas bolas —, que é
 * exatamente por que passou despercebido.
 */
export const BALL_IMPULSE_FACTOR: Fixed = F.div(F.ONE + BALL_RESTITUTION, F.fromInt(2))

/**
 * Quanto do efeito vertical vira velocidade no contato.
 *
 * Calibrado para dar controle de posição perceptível sem virar teleporte.
 */
export const SPIN_TRANSFER: Fixed = F.from(0.6)

/** Quanto do efeito sobra depois de ser usado num contato. */
export const SPIN_RETENTION: Fixed = F.from(0.35)

/** Quanto o efeito lateral desvia a saída da tabela. */
/**
 * Quanto o efeito lateral entorta a saída da tabela.
 *
 * Era 0,35, e com ele a faixa inteira do controle valia 3,9° — o jogador
 * aplicava o efeito máximo e a bola saía quase no mesmo lugar, o que na prática
 * apagava a tacada de tabela com efeito. O simulador de referência em Rust,
 * escrito a partir da literatura sem olhar este arquivo, dá ~11,4° para a mesma
 * faixa; 1,0 é o valor que nos põe lá.
 *
 * Escapar de um snooker pela tabela depende inteiramente disto.
 */
export const SPIN_CUSHION_EFFECT: Fixed = F.from(1.0)

/**
 * Perda de efeito por passo, pelo atrito do pano.
 *
 * Sobrevive da v2 porque o taco ainda a usa para descrever a aderência do
 * couro (`clothGripBps`). Na v3 ela já não governa o decaimento do efeito —
 * quem faz isso é `MU_SPIN` — mas continua modulando quanto de rotação a
 * tacada IMPRIME.
 */
export const SPIN_DECAY: Fixed = F.from(0.995)

/** Passo de integração: 1/240s. */
export const DT: Fixed = F.div(F.ONE, F.fromInt(240))

/**
 * Teto de passos por tacada.
 *
 * Sem ele, um bug de física viraria laço infinito no servidor. 240 passos por
 * segundo × 60 segundos é muito mais do que qualquer tacada real precisa.
 */
export const MAX_STEPS = 240 * 60

// ------------------------------------------------------------- caçapas

export type Pocket = { center: Vec; kind: 'corner' | 'side' }

/*
 * ------------------------------------------------------------- as bocas
 *
 * Até a v4 a tabela era uma parede reta contínua e a caçapa um círculo de
 * captura EM CIMA da linha da parede, metade dentro do pano. Funcionava, e
 * mentia: numa mesa de verdade a bola passa ENTRE as borrachas, entra na boca,
 * pode rebater nas faces (o "rattle"), e cai num buraco que fica fora do pano.
 * Sem boca, não há rattle, não há bola pendurada na caçapa, e o desenho tinha
 * de pôr o buraco onde a física engolia — em cima do pano, o que ninguém
 * reconhece como caçapa.
 *
 * As medidas são as da especificação de equipamento da WPA:
 *
 *   boca (de nariz a nariz)   canto 11,4 a 11,7 cm    meio 12,7 a 13,0 cm
 *   ângulo da face            canto 142°              meio 104°
 *   prateleira (da linha da
 *   boca até a queda)         canto 2,5 a 5,7 cm      meio 0 a 0,95 cm
 *
 * Sim, a boca do meio é a MAIOR. O canto só parece mais largo porque o corte
 * pega as duas tabelas.
 *
 * O nariz é onde a borracha termina, medido ao longo da tabela: no canto, a
 * partir do vértice do pano em cada tabela (8,2 cm dá 11,6 cm na diagonal);
 * no meio, a partir do centro da caçapa para cada lado (6,5 cm dá 13 cm).
 */

/** Nariz do canto: distância do vértice ao fim da borracha, em cada tabela. */
export const CORNER_NOSE: Fixed = F.from(0.082)

/** Nariz do meio: meia boca, do centro da caçapa ao fim da borracha. */
export const SIDE_NOSE: Fixed = F.from(0.065)

/**
 * Quanto cada face da boca desvia da linha da tabela, em radianos.
 *
 * A WPA dá o ângulo INTERNO entre a tabela e a face: 142° no canto, 104° no
 * meio. O desvio é o suplemento: 38° e 76°. No meio a face é quase
 * perpendicular à tabela — a boca do meio é um entalhe, não um funil.
 */
export const CORNER_JAW_DEVIATION = ((180 - 142) * Math.PI) / 180
export const SIDE_JAW_DEVIATION = ((180 - 104) * Math.PI) / 180

/**
 * Comprimento de cada face, do nariz até o fundo da boca.
 *
 * O fundo tem de ficar ALÉM do círculo de queda, contando o raio da bola:
 * a bola encosta no fundo com o centro a 2,9 cm dele, e se nesse ponto o
 * centro ainda não entrou no círculo, ela rebate em vez de cair. Foi o
 * primeiro defeito da v5: com 6 cm de face o fundo do canto passava a 0,1 cm
 * do vértice, e uma bola reta na diagonal voltava da caçapa. Com 12 cm o
 * fundo cruza a diagonal a 6 cm para fora, atrás do centro do buraco.
 */
export const CORNER_JAW_LENGTH = 0.12
export const SIDE_JAW_LENGTH = 0.05

/**
 * Prateleira: da linha da boca até a borda de queda, para FORA.
 *
 * O canto no meio da faixa da WPA; o meio praticamente sem prateleira, como
 * a norma manda — a bola cai assim que passa a linha dos narizes.
 */
const CORNER_SHELF = 0.04
const SIDE_SHELF = 0.005

/**
 * As seis caçapas: quatro nos cantos, duas no meio das tabelas longas.
 *
 * O centro fica FORA do pano, de modo que a borda de dentro do círculo de
 * captura coincida com a borda de queda (linha da boca + prateleira). Feita a
 * conta com o raio de 5 cm: canto a 2,3 cm para fora do vértice em cada
 * eixo, meio a 5,5 cm para fora da linha da tabela. Confere com uma foto de
 * cima de mesa de salão, onde o buraco do meio encosta na linha do pano e o
 * do canto avança 2 a 3 cm sobre o vértice.
 */
const cantoParaFora = (() => {
  const meiaBoca = F.toNumber(CORNER_NOSE) / 2 // linha da boca cruza a diagonal aqui
  const queda = meiaBoca - CORNER_SHELF / Math.SQRT2 // borda de queda, por eixo
  return F.toNumber(POCKET_RADIUS) / Math.SQRT2 - queda // centro = queda − r na diagonal
})()
const meioParaFora = SIDE_SHELF + F.toNumber(POCKET_RADIUS)

const W_M = F.toNumber(WIDTH)
const H_M = F.toNumber(HEIGHT)

export const POCKETS: readonly Pocket[] = [
  { center: V.fromNumbers(-cantoParaFora, -cantoParaFora), kind: 'corner' },
  { center: V.fromNumbers(W_M / 2, -meioParaFora), kind: 'side' },
  { center: V.fromNumbers(W_M + cantoParaFora, -cantoParaFora), kind: 'corner' },
  { center: V.fromNumbers(-cantoParaFora, H_M + cantoParaFora), kind: 'corner' },
  { center: V.fromNumbers(W_M / 2, H_M + meioParaFora), kind: 'side' },
  { center: V.fromNumbers(W_M + cantoParaFora, H_M + cantoParaFora), kind: 'corner' },
]

/** Índice da caçapa que capturou a bola, ou -1. */
export function pocketAt(position: Vec): number {
  for (let i = 0; i < POCKETS.length; i++) {
    if (V.distanceSq(position, POCKETS[i]!.center) <= POCKET_RADIUS_SQ) return i
  }
  return -1
}

// -------------------------------------------------------------- tabelas

/** Lados da mesa, na ordem: esquerda, direita, baixo, cima. */
export const enum Cushion {
  Left = 0,
  Right = 1,
  Bottom = 2,
  Top = 3,
}

/** Normal apontando para dentro da mesa. */
export const CUSHION_NORMALS: readonly Vec[] = [
  V.vec(F.ONE, 0),
  V.vec(-F.ONE, 0),
  V.vec(0, F.ONE),
  V.vec(0, -F.ONE),
]

/**
 * Limites que o CENTRO da bola pode alcançar antes de tocar cada tabela.
 *
 * Desde a v5 a simulação usa `WALLS`, não isto: dentro de uma boca a bola
 * passa destes limites. Continuam valendo para o que precisa de "área de
 * jogo" — bola na mão, mira, testes de sanidade.
 */
export const CUSHION_BOUNDS = {
  minX: BALL_RADIUS,
  maxX: WIDTH - BALL_RADIUS,
  minY: BALL_RADIUS,
  maxY: HEIGHT - BALL_RADIUS,
} as const

// ------------------------------------------------------------ paredes

/**
 * Um trecho reto de parede que a bola pode tocar.
 *
 * `a` → `b` é a superfície; `normal` aponta para o lado em que a bola fica.
 * `cushion` diz de que tabela o trecho faz parte — as faces e o fundo de uma
 * boca herdam a tabela do nariz de onde partem, para os eventos continuarem
 * dizendo "bateu na tabela de cima" em vez de inventar um quinto lado.
 *
 * `dir` e `length` são derivados, guardados para a simulação não os recalcular
 * a cada bola em cada passo.
 */
export type Wall = {
  a: Vec
  b: Vec
  normal: Vec
  dir: Vec
  length: Fixed
  cushion: Cushion
  /** Caixa envolvente do trecho, para descartar de longe sem fazer conta. */
  minX: Fixed
  maxX: Fixed
  minY: Fixed
  maxY: Fixed
}

function parede(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  ladoDaBola: { x: number; y: number },
  cushion: Cushion,
): Wall {
  const dx = bx - ax
  const dy = by - ay
  const comprimento = Math.hypot(dx, dy)
  // Normal perpendicular ao trecho, virada para o ponto de referência — o
  // centro da mesa nas tabelas, o meio da boca nas faces e no fundo.
  let nx = -dy / comprimento
  let ny = dx / comprimento
  const mx = (ax + bx) / 2
  const my = (ay + by) / 2
  if (nx * (ladoDaBola.x - mx) + ny * (ladoDaBola.y - my) < 0) {
    nx = -nx
    ny = -ny
  }
  return {
    a: V.fromNumbers(ax, ay),
    b: V.fromNumbers(bx, by),
    normal: V.fromNumbers(nx, ny),
    dir: V.fromNumbers(dx / comprimento, dy / comprimento),
    length: F.from(comprimento),
    cushion,
    minX: F.from(Math.min(ax, bx)),
    maxX: F.from(Math.max(ax, bx)),
    minY: F.from(Math.min(ay, by)),
    maxY: F.from(Math.max(ay, by)),
  }
}

/**
 * Todas as paredes da mesa: as tabelas entre os narizes, e em cada caçapa as
 * duas faces da boca mais o fundo que a fecha.
 *
 * Montadas em número real e convertidas UMA vez, como todas as constantes
 * deste arquivo. A ordem é fixa e faz parte da física: um empate de tempo de
 * contato é resolvido na ordem desta lista.
 */
export const WALLS: readonly Wall[] = (() => {
  const d = F.toNumber(CORNER_NOSE)
  const m = F.toNumber(SIDE_NOSE)
  const centro = { x: W_M / 2, y: H_M / 2 }
  const paredes: Wall[] = []

  // Tabelas longas (baixo e cima), cada uma em dois trechos.
  for (const [y, cushion] of [
    [0, Cushion.Bottom],
    [H_M, Cushion.Top],
  ] as const) {
    paredes.push(parede(d, y, W_M / 2 - m, y, centro, cushion))
    paredes.push(parede(W_M / 2 + m, y, W_M - d, y, centro, cushion))
  }
  // Tabelas curtas (esquerda e direita), inteiras entre os narizes.
  for (const [x, cushion] of [
    [0, Cushion.Left],
    [W_M, Cushion.Right],
  ] as const) {
    paredes.push(parede(x, d, x, H_M - d, centro, cushion))
  }

  // Cantos. `sx`/`sy` dizem para que lado a caçapa está (−1 ou +1).
  for (const [sx, sy, horizontal, vertical] of [
    [-1, -1, Cushion.Bottom, Cushion.Left],
    [1, -1, Cushion.Bottom, Cushion.Right],
    [-1, 1, Cushion.Top, Cushion.Left],
    [1, 1, Cushion.Top, Cushion.Right],
  ] as const) {
    const vx = sx < 0 ? 0 : W_M
    const vy = sy < 0 ? 0 : H_M
    const boca = { x: vx - sx * (d / 2), y: vy - sy * (d / 2) }
    const cosD = Math.cos(CORNER_JAW_DEVIATION)
    const sinD = Math.sin(CORNER_JAW_DEVIATION)
    // Face que parte do nariz da tabela longa: anda rumo ao vértice e sai
    // para fora do pano.
    const n1 = { x: vx - sx * d, y: vy }
    const e1 = { x: n1.x + sx * cosD * CORNER_JAW_LENGTH, y: n1.y + sy * sinD * CORNER_JAW_LENGTH }
    // Face que parte do nariz da tabela curta, simétrica.
    const n2 = { x: vx, y: vy - sy * d }
    const e2 = { x: n2.x + sx * sinD * CORNER_JAW_LENGTH, y: n2.y + sy * cosD * CORNER_JAW_LENGTH }
    paredes.push(parede(n1.x, n1.y, e1.x, e1.y, boca, horizontal))
    paredes.push(parede(n2.x, n2.y, e2.x, e2.y, boca, vertical))
    paredes.push(parede(e1.x, e1.y, e2.x, e2.y, boca, horizontal))
  }

  // Meios.
  for (const [sy, cushion] of [
    [-1, Cushion.Bottom],
    [1, Cushion.Top],
  ] as const) {
    const y = sy < 0 ? 0 : H_M
    const cx = W_M / 2
    const boca = { x: cx, y }
    const cosD = Math.cos(SIDE_JAW_DEVIATION)
    const sinD = Math.sin(SIDE_JAW_DEVIATION)
    const e1 = { x: cx - m + cosD * SIDE_JAW_LENGTH, y: y + sy * sinD * SIDE_JAW_LENGTH }
    const e2 = { x: cx + m - cosD * SIDE_JAW_LENGTH, y: y + sy * sinD * SIDE_JAW_LENGTH }
    paredes.push(parede(cx - m, y, e1.x, e1.y, boca, cushion))
    paredes.push(parede(cx + m, y, e2.x, e2.y, boca, cushion))
    paredes.push(parede(e1.x, e1.y, e2.x, e2.y, boca, cushion))
  }

  return paredes
})()

/**
 * Posição está dentro da área jogável, considerando o raio da bola?
 *
 * "Área jogável" é o pano entre as tabelas: é onde a bola na mão pode ser
 * posta e onde a mira faz sentido. Uma bola dentro de uma boca NÃO está aqui
 * — para "ainda está na mesa" use `isOnTable`.
 */
export function isInsideBounds(position: Vec): boolean {
  return (
    position.x >= CUSHION_BOUNDS.minX &&
    position.x <= CUSHION_BOUNDS.maxX &&
    position.y >= CUSHION_BOUNDS.minY &&
    position.y <= CUSHION_BOUNDS.maxY
  )
}

/**
 * Até onde do centro da caçapa uma bola pode estar e ainda ser "boca".
 *
 * O ponto mais longe é uma bola encostada no NARIZ pelo lado de dentro da
 * boca: no canto o nariz fica a 10,7 cm do centro da caçapa, e a bola mais
 * 2,9 cm de raio. 14 cm cobre isso com folga e continua bem abaixo da
 * distância a qualquer ponto do pano que já não esteja em `isInsideBounds`.
 */
const THROAT_REACH_SQ: Fixed = F.sqr(F.from(0.14))

/**
 * A bola continua na mesa — no pano ou dentro de uma boca?
 *
 * É o teste de sanidade "nenhuma bola atravessou parede". Uma bola parada
 * na boca, sem cair, é jogo legítimo (a "bola pendurada"), e está na mesa.
 */
export function isOnTable(position: Vec): boolean {
  if (isInsideBounds(position)) return true
  for (const pocket of POCKETS) {
    if (V.distanceSq(position, pocket.center) <= THROAT_REACH_SQ) return true
  }
  return false
}

// ---------------------------------------------------- posições iniciais

/** Linha da cabeça: onde a branca é colocada no saque. */
export const HEAD_STRING_X: Fixed = F.div(WIDTH, F.fromInt(4))

/** Ponto de pé: vértice do triângulo. */
export const FOOT_SPOT: Vec = V.vec(
  F.mul(WIDTH, F.from(0.75)),
  F.div(HEIGHT, F.fromInt(2)),
)

export const CUE_SPOT: Vec = V.vec(HEAD_STRING_X, F.div(HEIGHT, F.fromInt(2)))
